/**
 * PlanGate — wraps all authenticated app routes.
 *
 * On every login it fetches /api/billing/status once and:
 *   • No plan  → renders PlanSelectionModal (non-dismissible) over the page
 *   • Expired  → renders PaywallModal (non-dismissible) over the page
 *   • Active   → renders children normally
 *
 * Public paths (landing, OAuth callback, shared team views, invites) are
 * always rendered without a billing check.
 */
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import PlanSelectionModal from '@/components/PlanSelectionModal';
import PaywallModal from '@/components/PaywallModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Routes that bypass the billing gate entirely
const isPublicPath = (pathname) => {
  if (pathname === '/') return true;
  if (pathname.startsWith('/auth/')) return true;
  if (pathname.startsWith('/invite/')) return true;
  if (pathname.startsWith('/onboarding')) return true;
  if (pathname.startsWith('/billing/')) return true;
  if (pathname.includes('/shared')) return true;  // /teams/:id/shared
  return false;
};

export default function PlanGate({ children }) {
  const { user, loading: authLoading } = useAuth();
  const location = useLocation();
  const [planStatus, setPlanStatus] = useState(null);
  const [planLoading, setPlanLoading] = useState(false);

  const skipGate = isPublicPath(location.pathname);

  useEffect(() => {
    if (!user || skipGate) return;

    setPlanLoading(true);
    axios
      .get(`${API}/billing/status`, { withCredentials: true })
      .then(res => setPlanStatus(res.data))
      .catch(() => {}) // non-critical — if this fails, don't block the user
      .finally(() => setPlanLoading(false));
    // Re-fetch only when the user object changes (login / logout)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // 1. Not logged in, or still resolving auth, or public path → just render
  if (authLoading || !user || skipGate) return children;

  // 2. Billing not fetched yet — render children without blocking to avoid flash
  if (planLoading || planStatus === null) return children;

  // 3. User has no plan at all → show plan selection gate
  if (!planStatus.has_plan) {
    return (
      <>
        {children}
        <PlanSelectionModal
          open={true}
          onActivated={() =>
            setPlanStatus(prev => ({
              ...prev,
              has_plan: true,
              plan: 'trial',
              status: 'active',
              days_remaining: 7,
            }))
          }
        />
      </>
    );
  }

  // 4. Trial/plan expired or cancelled → show paywall gate
  if (planStatus.status === 'expired' || planStatus.status === 'cancelled') {
    return (
      <>
        {children}
        <PaywallModal open={true} planStatus={planStatus} />
      </>
    );
  }

  // 5. Active plan → render normally
  return children;
}
